// DraftCloseConfirmModal — three-action dirty-close prompt for the
// draft scratchpad. Replaces `window.confirm` so the user explicitly
// chooses between save+close, discard+close, and continue-editing
// instead of a binary OK/Cancel that silently maps to discard.
//
// @aiPurpose Three-action close confirmation for the draft drawer.
// @aiInvariant Actions are rendered as a vertical full-width stack in
//   the modal body, not in `Modal.footer`, so the Russian copy never
//   overflows the sheet on desktop or mobile widths.
// @aiInvariant `onSave` is awaited; while it runs the buttons stay
//   disabled and the inline status reflects `saving`. A failed save
//   shows the error and keeps the modal open so the user can retry,
//   discard, or cancel — never silently discards on save failure.
// @aiInvariant Discard requires two deliberate activations. First
//   activation only flips local `discardArmed` state — no cleanup,
//   close, navigation follow-up, or store mutation runs until the
//   second accepted activation. A 300ms minimum delay guards against
//   accidental keyboard double-press (Enter/Space).
// @aiInvariant Armed state resets only on the explicit transitions
//   listed below: continue-editing, save-start, modal close/reopen, or
//   when discard becomes unavailable because the modal goes busy. It
//   does NOT reset on hover, focus-out, body clicks, or a timer.
// @aiInvariant While `saving || uploading`, dismissal via the modal
//   close icon, Escape, and backdrop is blocked at the Modal level
//   via `dismissDisabled`. The parent `DraftDrawer` enforces the same
//   discipline at the drawer level for its own Escape/backdrop.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal } from '../../../ui/index.js';

export interface DraftCloseConfirmModalProps {
  open: boolean;
  persisted: boolean;
  saving: boolean;
  /** True while at least one item-photo upload is racing. */
  uploading: boolean;
  saveError: string | null;
  onSaveAndClose: () => void;
  onDiscardAndClose: () => void;
  onCancel: () => void;
}

const DISCARD_CONFIRM_MIN_DELAY_MS = 300;

export function DraftCloseConfirmModal({
  open,
  persisted,
  saving,
  uploading,
  saveError,
  onSaveAndClose,
  onDiscardAndClose,
  onCancel,
}: DraftCloseConfirmModalProps) {
  const { t } = useTranslation(['requests']);
  // INVARIANT (#521): both save+close and discard+close are blocked
  // while a photo PUT is in flight — committing either path before
  // the upload settles would race with `endPhotoUpload` and leak the
  // resolved key past cleanup.
  const buttonsDisabled = saving || uploading;
  const dismissDisabled = saving || uploading;

  const [discardArmed, setDiscardArmed] = useState(false);
  const armedAtMsRef = useRef<number | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  // Reset armed state on close/reopen so a stale armed flag never
  // survives across modal lifecycles.
  useEffect(() => {
    if (!open) {
      setDiscardArmed(false);
      armedAtMsRef.current = null;
    }
  }, [open]);

  // If discard becomes unavailable mid-flight (saving or upload starts
  // while armed), drop the armed state so the user re-confirms after
  // the busy state clears.
  useEffect(() => {
    if (buttonsDisabled && discardArmed) {
      setDiscardArmed(false);
      armedAtMsRef.current = null;
    }
  }, [buttonsDisabled, discardArmed]);

  function handleDiscardClick(): void {
    if (buttonsDisabled) return;
    if (!discardArmed) {
      setDiscardArmed(true);
      armedAtMsRef.current = Date.now();
      return;
    }
    const armedAt = armedAtMsRef.current;
    if (armedAt !== null && Date.now() - armedAt < DISCARD_CONFIRM_MIN_DELAY_MS) {
      // Swallow the rapid double-activation; user must wait the
      // minimum delay before the second accepted activation.
      return;
    }
    onDiscardAndClose();
  }

  function handleSaveClick(): void {
    if (buttonsDisabled) return;
    // Save start resets armed discard so a stale "armed" never
    // confuses post-save UI.
    if (discardArmed) {
      setDiscardArmed(false);
      armedAtMsRef.current = null;
    }
    onSaveAndClose();
  }

  function handleContinueClick(): void {
    // INVARIANT (#529): cancel must mirror Save/Discard busy discipline.
    // The parent `onConfirmCancel` already short-circuits on the same
    // busy states; the visible button must be disabled too so the UI
    // never promises an action that the guard would silently swallow.
    if (buttonsDisabled) return;
    if (discardArmed) {
      setDiscardArmed(false);
      armedAtMsRef.current = null;
    }
    onCancel();
  }

  const discardLabel = discardArmed
    ? t('requests:draft.closeConfirm.confirmCloseWithoutSaving')
    : t('requests:draft.closeConfirm.closeWithoutSaving');

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={
        persisted
          ? t('requests:draft.closeConfirm.titlePersisted')
          : t('requests:draft.closeConfirm.titleUnsaved')
      }
      width={460}
      dismissDisabled={dismissDisabled}
      initialFocusRef={primaryRef}
    >
      <div style={{ color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.55 }}>
        {persisted
          ? t('requests:draft.closeConfirm.bodyPersisted')
          : t('requests:draft.closeConfirm.bodyUnsaved')}
      </div>
      {uploading && (
        <div
          role="status"
          style={{
            marginTop: 12,
            color: 'var(--fg-3)',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          {t('requests:draft.closeConfirm.uploadingHint')}
        </div>
      )}
      {saveError && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            color: 'var(--status-error-fg)',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          {saveError}
        </div>
      )}

      {/* Vertical full-width action stack lives in the modal body, not
          in Modal.footer, so the long Russian labels never overflow
          the 460px sheet. The stack order matches Resolved Decision
          #3: save / continue / discard. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 16,
        }}
      >
        <Button
          ref={primaryRef}
          kind="primary"
          onClick={handleSaveClick}
          disabled={buttonsDisabled}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            justifyContent: 'center',
            whiteSpace: 'normal',
          }}
        >
          {saving ? t('requests:draft.saving') : t('requests:draft.closeConfirm.saveAndClose')}
        </Button>
        <Button
          kind="ghost"
          onClick={handleContinueClick}
          disabled={buttonsDisabled}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            justifyContent: 'center',
            whiteSpace: 'normal',
          }}
        >
          {t('requests:draft.closeConfirm.continueEditing')}
        </Button>
      </div>

      {/* Visual separator between safe and destructive actions, plus
          a polite live region that announces the armed state to
          screen readers. The hint <div> is always in the DOM so
          assistive tech can pick up the content swap. */}
      <div
        style={{
          marginTop: 20,
          paddingTop: 16,
          borderTop: '1px solid var(--border-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Button
          kind="danger"
          icon="alert-triangle"
          onClick={handleDiscardClick}
          disabled={buttonsDisabled}
          aria-describedby="draft-close-confirm-discard-hint"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            justifyContent: 'center',
            whiteSpace: 'normal',
          }}
        >
          {discardLabel}
        </Button>
        <div
          id="draft-close-confirm-discard-hint"
          aria-live="polite"
          aria-atomic="true"
          style={{
            minHeight: 16,
            color: 'var(--status-error-fg)',
            fontSize: 12,
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {discardArmed && (
            <>
              <Icon name="alert-triangle" size={12} />
              <span>{t('requests:draft.closeConfirm.discardArmedHint')}</span>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
